import { Box, Typography } from "@mui/material";
import { Description, CloudUpload, Link as LinkIcon, Twitter, YouTube, FolderShared, Mic, Check } from "@mui/icons-material";

// Step configuration with icons and titles
const getStepConfig = (component: string) => {
  const configs: Record<string, { icon: any; title: string; description: string }> = {
    'roleSelection': { icon: Description, title: 'Get Started', description: 'Tell us about yourself' },
    'basicTraining': { icon: Description, title: 'Basic Information', description: 'Set up your AI persona basics' },
    'documents': { icon: CloudUpload, title: 'Upload Documents', description: 'Share articles, blogs, and written content' },
    'weblinks': { icon: LinkIcon, title: 'Add Web Links', description: 'Connect websites and online resources' },
    'googleDrive': { icon: FolderShared, title: 'Google Drive', description: 'Import files from your Google Drive' },
    'youtube': { icon: YouTube, title: 'YouTube Videos', description: 'Add your YouTube content' },
    'twitter': { icon: Twitter, title: 'Social Media', description: 'Connect your social accounts' },
    'voiceSample': { icon: Mic, title: 'Voice Sample', description: 'Record or upload your voice' },
    'premiumTraining': { icon: Description, title: 'Advanced Training', description: 'Fine-tune your AI persona' },
    'footer': { icon: Check, title: 'Complete Setup', description: 'Review and finish' },
  };
  return configs[component] || { icon: Description, title: component, description: 'Complete this step' };
};

interface StepCounterProps {
  components: string[];
  currentStep: number;
}

const StepCounter: React.FC<StepCounterProps> = ({ components, currentStep }) => {
  return (
    <>
      {/* Mobile Horizontal Stepper - Top */}
      <Box
        sx={{
          display: { xs: 'flex', md: 'none' },
          overflowX: 'auto',
          borderBottom: (theme) => `1px solid ${theme.palette.divider}`,
          bgcolor: 'background.paper',
          py: 2,
          px: 2,
          width: '100%',
          '&::-webkit-scrollbar': {
            height: 4,
          },
          '&::-webkit-scrollbar-track': {
            bgcolor: (theme) => theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.1)' : '#f0f0f0',
          },
          '&::-webkit-scrollbar-thumb': {
            bgcolor: (theme) => theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.3)' : '#c0c0c0',
            borderRadius: 2,
          },
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 0,
            minWidth: 'fit-content',
            mx: 'auto',
          }}
        >
          {components.map((component, index) => {
            const stepConfig = getStepConfig(component);
            const isActive = index === currentStep;
            const isCompleted = index < currentStep;

            return (
              <Box
                key={index}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                }}
              >
                {/* Icon Circle */}
                <Box
                  sx={{
                    width: 32,
                    height: 32,
                    minWidth: 32,
                    borderRadius: '50%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    bgcolor: isCompleted ? 'success.main' : 'background.paper',
                    border: (theme) => isCompleted ? `2px solid ${theme.palette.success.main}` : isActive ? `2px solid ${theme.palette.primary.main}` : `2px solid ${theme.palette.divider}`,
                    color: isCompleted ? 'success.contrastText' : isActive ? 'primary.main' : 'text.disabled',
                    transition: 'all 0.3s ease-in-out',
                  }}
                >
                  {isCompleted ? (
                    <Check sx={{ fontSize: 16 }} />
                  ) : (
                    <stepConfig.icon sx={{ fontSize: 16 }} />
                  )}
                </Box>

                {/* Connecting Line for Mobile */}
                {index < components.length - 1 && (
                  <Box
                    sx={{
                      width: 24,
                      height: 2,
                      bgcolor: isCompleted ? 'success.main' : 'divider',
                      transition: 'background-color 0.3s ease-in-out',
                      mx: 0.25,
                    }}
                  />
                )}
              </Box>
            );
          })}
        </Box>
      </Box>

      {/* Right Side - Vertical Stepper (Desktop) */}
      <Box
        sx={{
          width: { xs: '0px', md: '320px', lg: '380px' },
          display: { xs: 'none', md: 'flex' },
          flexDirection: 'column',
          bgcolor: 'background.paper',
          p: 4,
          gap: 0,
        }}
      >
        {/* Vertical Steps */}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0, pt: 2 }}>
          {components.map((component, index) => {
            const stepConfig = getStepConfig(component);
            const isActive = index === currentStep;
            const isCompleted = index < currentStep;

            return (
              <Box key={index}>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: 2,
                    position: 'relative',
                  }}
                >
                  {/* Icon Circle */}
                  <Box
                    sx={{
                      width: 40,
                      height: 40,
                      minWidth: 40,
                      borderRadius: '50%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      bgcolor: isCompleted ? 'success.main' : 'background.default',
                      border: (theme) => isCompleted ? `2px solid ${theme.palette.success.main}` : isActive ? `2px solid ${theme.palette.primary.main}` : `2px solid ${theme.palette.divider}`,
                      color: isCompleted ? 'success.contrastText' : isActive ? 'primary.main' : 'text.disabled',
                      transition: 'all 0.3s ease-in-out',
                      zIndex: 1,
                    }}
                  >
                    {isCompleted ? (
                      <Check sx={{ fontSize: 20 }} />
                    ) : (
                      <stepConfig.icon sx={{ fontSize: 20 }} />
                    )}
                  </Box>

                  {/* Step Content */}
                  <Box sx={{ flex: 1, pb: index < components.length - 1 ? 4 : 0 }}>
                    <Typography
                      variant="body1"
                      sx={{
                        fontWeight: isActive ? 600 : 400,
                        color: isActive ? 'text.primary' : 'text.secondary',
                        mb: 0.5,
                      }}
                    >
                      {stepConfig.title}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {stepConfig.description}
                    </Typography>
                  </Box>

                  {/* Connecting Line */}
                  {index < components.length - 1 && (
                    <Box
                      sx={{
                        position: 'absolute',
                        left: 19,
                        top: 46,
                        width: 2,
                        height: 'calc(100% - 6px)',
                        bgcolor: isCompleted ? 'success.main' : 'divider',
                        transition: 'background-color 0.3s ease-in-out',
                      }}
                    />
                  )}
                </Box>
              </Box>
            );
          })}
        </Box>
      </Box>
    </>
  );
};

export default StepCounter;
